import { useState } from "react";
import { ArrowUpRight, Megaphone, X } from "lucide-react";
import { noticeStatus, pickNotices, useSite } from "@/lib/content";
import { getLang, t } from "@/lib/i18n";

const KEY = (id) => `woodia-notice-dismissed-${id}`;
const isDismissed = (id) => {
  try {
    return localStorage.getItem(KEY(id)) === "1";
  } catch (e) {
    return false;
  }
};

function NoticeCard({ n, isPreview, onDismiss }) {
  const lang = getLang();
  const status = noticeStatus(n, lang);
  const title = n.title?.[lang];
  const text = n.text?.[lang];
  const label = n.link_label?.[lang];
  return (
    <aside className="relative rounded-2xl border border-line bg-white shadow-[0_20px_50px_-30px_rgba(30,58,43,0.5)] pl-6 pr-14 py-5 sm:pl-8 sm:py-6 border-l-4 border-l-honey" aria-label={t("notice.label")} data-testid={`notice-${n.notice_id}`}>
      <div className="flex items-start gap-4">
        <Megaphone className="h-5 w-5 mt-1 shrink-0 text-honey-ink" aria-hidden="true" />
        <div className="space-y-1.5 min-w-0">
          {isPreview && (
            <span className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${status === "live" ? "bg-[#ECFDF3] text-[#05603A]" : "bg-[#FEF3C7] text-[#78350F]"}`} data-testid={`notice-${n.notice_id}-status`}>
              {t(`notice.status.${status}`)}
            </span>
          )}
          <p className="font-serif text-2xl font-semibold leading-tight text-forest">{title || t("preview.pending")}</p>
          {text && <p className="text-[15px] leading-relaxed text-ink-muted">{text}</p>}
          {n.link_url && label && (
            <a href={n.link_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 min-h-[44px] font-semibold text-forest underline-offset-4 hover:underline" data-testid={`notice-${n.notice_id}-link`}>
              {label} <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </a>
          )}
        </div>
      </div>
      <button type="button" onClick={onDismiss} aria-label={t("notice.close")} className="absolute top-2 right-2 inline-flex h-11 w-11 items-center justify-center rounded-full text-ink-muted hover:bg-cream-2 hover:text-forest transition-colors" data-testid={`notice-${n.notice_id}-dismiss`}>
        <X className="h-5 w-5" aria-hidden="true" />
      </button>
    </aside>
  );
}

export default function Notices() {
  const { notices, isPreview } = useSite();
  const [hidden, setHidden] = useState({});
  const list = pickNotices(notices, isPreview).filter((n) => !hidden[n.notice_id] && !isDismissed(n.notice_id));
  if (!list.length) return null;
  const dismiss = (id) => {
    try {
      localStorage.setItem(KEY(id), "1");
    } catch (e) {
      /* storage unavailable */
    }
    setHidden({ ...hidden, [id]: true });
  };
  return (
    <section className="safe-x max-w-7xl mx-auto -mt-10 sm:-mt-14 relative z-10 space-y-3 fade-in" data-testid="home-notices">
      {list.map((n) => <NoticeCard key={n.notice_id} n={n} isPreview={isPreview} onDismiss={() => dismiss(n.notice_id)} />)}
    </section>
  );
}
